import { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import CachedIcon from '@mui/icons-material/Cached';
import { CircularProgress, Divider, Grid } from '@mui/material';
import { Stack } from '@mui/system';
import { useFormik } from 'formik';
import * as yup from 'yup';

import useImages from '../../api/useImages';
import useUsers from '../../api/useUsers';
import { TRIMMED_INPUT_REGEX } from '../../constants/constants';
import { EAssetType, EFeatureFlag, EProductType } from '../../constants/enums';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { UploadsTooltips } from '../../hooks/useUpload';
import { min, required } from '../../utils/errorsTextHelper';
import AcCard from '../AcCard/AcCard';
import AcContentWrapper from '../AcContentWrapper/AcContentWrapper';
import AcInput from '../AcInput/AcInput';
import AcSelect from '../AcSelect/AcSelect';
import AcViewWrapper from '../AcViewWrapper/AcViewWrapper';
import PageTopBar from '../Topbar/PageTopBar';

import { ImageObject, ProductsFormProps } from './ProductsForm.types';

import './style.scss';
import '../../style/forms.scss';

const ProductsForm = ({
  headline,
  buttonText,
  postProduct,
  data,
  emptyCache,
  isLoading = false,
  edit,
  dup
}: ProductsFormProps) => {
  const { publisherId } = useParams();
  const pictures = useImages(publisherId).getImages;
  const { fetchFeatureFlags } = useUsers({ publisherId });
  const [disabled, setDisabled] = useState(true);
  const navigate = useNavigate();

  const isProductDescriptionEnabled =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_PRODUCT_DESCRIPTION
    ];

  const productSchema = yup.object().shape({
    name: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
      .required(required('Name'))
      .min(3, min('Name', 3)),
    publisherProductId: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required(required('External ID'))
      .min(2, min('External ID', 2)),
    type: yup.string(),
    textFontColorHex: yup.string().required(required('Font color')),
    formImages: yup.object().shape({
      product: yup.string().url().required(required('Product image')),
      productPrefix: yup.string().url()
    }),
    prefix: yup.string(),
    suffix: yup.string(),
    displayName: yup.string(),
    description: yup.string()
  });

  const {
    values,
    errors,
    touched,
    dirty,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    submitForm,
    setFieldValue
  } = useFormik({
    initialValues: {
      formImages: {
        product:
          data?.images.filter(
            (image: ImageObject) => image.type === EAssetType.PRODUCT
          )[0]?.url || '',
        productPrefix:
          data?.images.filter(
            (image: ImageObject) => image.type === EAssetType.PRODUCT_PREFIX
          )[0]?.url || ''
      } as Record<string, string>,
      name: data?.name ? `${data?.name}${dup ? '_copy' : ''}` : '',
      publisherProductId: data?.publisherProductId || '',
      type: data?.type || 'Quantity',
      prefix: data?.prefix || '',
      suffix: data?.suffix || '',
      textFontColorHex: data?.textFontColorHex || '#FFFFFF',
      publisherId: '63e9ff6f062dde4c284603ca',
      displayName: data?.displayName || '',
      description: data?.description || ''
    },
    validationSchema: productSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const images = Object.keys(values.formImages).map((key: string) => ({
        type: key as EAssetType,
        url: values.formImages[key]
      }));

      const payload = {
        images,
        ...values
      };

      postProduct(payload, () => setSubmitting(false));
    }
  });

  const backToList = () => {
    if (emptyCache) emptyCache();
    navigate('../');
  };

  useEffect(() => {
    setDisabled(!isValid || !dirty || isSubmitting);
  }, [isValid, dirty, isSubmitting]);

  useUnsavedChanges({ dirty });

  const handleImgSelectOpen = useCallback((testId: string) => {
    setTimeout(() => {
      const listRoot = document.querySelector('.MuiList-root');
      if (listRoot) {
        const existingTestId = listRoot.getAttribute('data-testid');
        if (!existingTestId || existingTestId !== testId) {
          listRoot.setAttribute('data-testid', testId);
        }
      }
    }, 0);
  }, []);

  const renderImageItem = (picture: any) => (
    <Stack className="formContent-imageSelected" direction="row" spacing={2}>
      <img alt={picture.name} src={picture.url} />
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginLeft: 0
        }}
      >
        {picture.name}
      </div>
    </Stack>
  );

  const getImageItems = (assetType: EAssetType) => {
    if (pictures.isLoading || !pictures.data) return [];

    return pictures.data
      .filter((p: any) => p.type === assetType)
      .map((picture: any) => ({
        content: picture.name,
        key: picture.url,
        value: picture.url,
        renderFunction: () => renderImageItem(picture)
      }));
  };

  if (isLoading) {
    return (
      <AcViewWrapper>
        <AcContentWrapper className="formContent">
          <Stack
            height="calc(100vh - 180px)"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Stack>
        </AcContentWrapper>
      </AcViewWrapper>
    );
  }

  return (
    <AcViewWrapper
      header={
        <PageTopBar
          headline={headline}
          buttons={[
            {
              text: buttonText,
              action: submitForm,
              icon: edit ? (
                <CachedIcon className={disabled ? 'disable' : ''} />
              ) : undefined
            }
          ]}
          backFunction={backToList}
          disable={disabled}
        />
      }
    >
      <AcContentWrapper className="formContent">
        <form id="productForm">
          <Grid container mt={'30px'}>
            <Grid item xs={5} className="formContent-minWIdth">
              <AcCard stackContainer={false} title="General">
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={6}>
                    <AcInput
                      required
                      header="Name"
                      name="name"
                      variant="outlined"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name ? errors.name?.toString() : ''}
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <AcInput
                      header="Display Name"
                      name="displayName"
                      variant="outlined"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.displayName && Boolean(errors.displayName)}
                      tooltip="Add your display name for this product"
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <AcInput
                      header="Description"
                      name="description"
                      variant="outlined"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      tooltip={
                        !isProductDescriptionEnabled
                          ? 'This feature is not available for the template defined for this store'
                          : 'Add a description for this product'
                      }
                      disabled={!isProductDescriptionEnabled}
                      sx={{
                        ...(!isProductDescriptionEnabled && {
                          '& .MuiInputBase-root': {
                            backgroundColor: '#f5f5f5'
                          }
                        })
                      }}
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <AcInput
                      required
                      header="External ID"
                      name="publisherProductId"
                      variant="outlined"
                      value={values.publisherProductId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.publisherProductId &&
                        Boolean(errors.publisherProductId)
                      }
                      helperText={
                        touched.publisherProductId
                          ? errors.publisherProductId?.toString()
                          : ''
                      }
                    />
                  </Grid>
                  <Divider style={{ marginTop: '16px', marginBottom: 0 }} />
                  <Grid xs={6} item>
                    <AcSelect
                      header="Type"
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.type && Boolean(errors.type)}
                      items={Object.values(EProductType).map(
                        (type: string) => ({
                          key: type,
                          value: type,
                          content: type
                        })
                      )}
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <AcInput
                      header="Prefix"
                      name="prefix"
                      variant="outlined"
                      value={values.prefix}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.prefix && Boolean(errors.prefix)}
                      helperText={
                        touched.prefix ? errors.prefix?.toString() : ''
                      }
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <AcInput
                      header="Suffix"
                      name="suffix"
                      variant="outlined"
                      value={values.suffix}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.suffix && Boolean(errors.suffix)}
                      helperText={
                        touched.suffix ? errors.suffix?.toString() : ''
                      }
                    />
                  </Grid>
                </Grid>
              </AcCard>

              <Divider />

              <AcCard stackContainer={false} title="Font Color">
                <Grid xs={12} item>
                  <AcInput
                    header="Fill Color"
                    type="color"
                    name="textFontColorHex"
                    variant="outlined"
                    value={values.textFontColorHex}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={
                      touched.textFontColorHex &&
                      Boolean(errors.textFontColorHex)
                    }
                    helperText={
                      touched.textFontColorHex
                        ? errors.textFontColorHex?.toString()
                        : ''
                    }
                    tooltip="This color will highlight the quantity of this product in the bundles"
                  />
                </Grid>
              </AcCard>

              <Divider />

              <AcCard
                stackContainer={false}
                title="Product Assets"
                description="This image will appear above the item's quantity."
              >
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid xs={6} item>
                    <AcSelect
                      required
                      header="Product Image"
                      name="formImages.product"
                      value={values.formImages.product}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onOpen={() => handleImgSelectOpen('dashProductImageList')}
                      disabled={pictures.isLoading || pictures.isRefetching}
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('formImages.product', url);
                        },
                        uploadType: EAssetType.PRODUCT,
                        uploadMessage: UploadsTooltips[EAssetType.PRODUCT]
                      }}
                      error={
                        touched.formImages?.product &&
                        Boolean(errors.formImages?.product)
                      }
                      items={getImageItems(EAssetType.PRODUCT)}
                      tooltip="For optimized display, make sure your image are at least 300px with 1:1 aspect ratio"
                      uploadBtnTestId="dashProductImageUploadBtn"
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <AcSelect
                      header="Prefix Image"
                      name="formImages.productPrefix"
                      value={values.formImages.productPrefix}
                      onClear={() =>
                        setFieldValue('formImages.productPrefix', '')
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onOpen={() =>
                        handleImgSelectOpen('dashProductPrefixImageList')
                      }
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('formImages.productPrefix', url);
                        },
                        uploadType: EAssetType.PRODUCT_PREFIX,
                        uploadMessage:
                          UploadsTooltips[EAssetType.PRODUCT_PREFIX]
                      }}
                      error={
                        touched.formImages?.productPrefix &&
                        Boolean(errors.formImages?.productPrefix)
                      }
                      items={getImageItems(EAssetType.PRODUCT_PREFIX)}
                      tooltip="For optimized display, make sure your image are at least 300px with 1:1 aspect ratio"
                      uploadBtnTestId="dashProductPrefixImageUploadBtn"
                    />
                  </Grid>
                </Grid>
              </AcCard>
            </Grid>
          </Grid>
        </form>
      </AcContentWrapper>
    </AcViewWrapper>
  );
};

export default ProductsForm;
